import React, { useMemo, useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { CreateConsultationModal } from '../components/consultation/CreateConsultationModal';
import { ConsultationNotification } from '../components/consultation/ConsultationNotification';
import { ConsultationNotificationSettings } from '../components/consultation/ConsultationNotificationSettings';
import { useConsultas } from '../hooks/useConsultas';
import { useAuth } from '../contexts/AuthContext';
import { 
  openGoogleCalendar, 
  createConsultationEvent,
  createGoogleCalendarEvent,
  syncConsultationsToGoogleCalendar,
  listGoogleCalendarEvents
} from '../services/googleCalendarService';
import { 
  initiateAuth, 
  isAuthenticated, 
  removeToken 
} from '../services/googleCalendarOAuthService';
import './Pages.css';

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;

function getDiaLabel(dateIso: string) {
  const d = new Date(dateIso);
  if (isNaN(d.getTime())) return 'Dom';
  return DIAS[d.getDay()];
}

function formatHora(iso: string) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const Schedule: React.FC = () => {
  const { consultas, loading, refresh } = useConsultas(30);
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isGoogleCalendarConnected, setIsGoogleCalendarConnected] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);

  const grouped = useMemo(() => {
    const byDay: Record<string, typeof consultas> = {};
    DIAS.forEach((d) => (byDay[d] = []));

    consultas.forEach((c) => {
      const dia = getDiaLabel(c.dataIso);
      if (!byDay[dia]) byDay[dia] = [];
      byDay[dia].push(c);
    });

    // ordenar dentro de cada dia
    DIAS.forEach((d) => {
      byDay[d].sort(
        (a, b) =>
          new Date(a.dataIso).getTime() - new Date(b.dataIso).getTime()
      );
    });

    return byDay;
  }, [consultas]);

  // Verificar se Google Calendar está conectado
  useEffect(() => {
    const checkConnection = async () => {
      if (user?.uid) {
        const connected = await isAuthenticated(user.uid);
        setIsGoogleCalendarConnected(connected);
      }
    };
    checkConnection();
  }, [user]);

  const handleSuccess = () => {
    if (refresh) {
      refresh();
    } else {
      window.location.reload();
    }
  };

  const connectGoogleCalendar = () => {
    if (!user?.uid) {
      alert('Você precisa estar logado para conectar o Google Calendar');
      return;
    }
    initiateAuth(user.uid);
  };

  const disconnectGoogleCalendar = async () => {
    if (!user?.uid) return;
    
    if (window.confirm('Tem certeza que deseja desconectar o Google Calendar?')) {
      await removeToken(user.uid);
      setIsGoogleCalendarConnected(false);
      setSyncStatus('Google Calendar desconectado com sucesso');
      setTimeout(() => setSyncStatus(null), 3000);
    }
  };

  const syncToGoogleCalendar = async () => {
    if (!user?.uid) return;
    
    setIsSyncing(true);
    setSyncStatus('Sincronizando consultas...');
    
    try {
      const consultationsToSync = consultas.map(c => ({
        id: c.id,
        pacienteNome: c.pacienteNome,
        tipo: c.tipo,
        dataIso: c.dataIso,
        notes: c.notes,
        googleEventId: (c as any).googleEventId,
      }));

      const synced = await syncConsultationsToGoogleCalendar(user.uid, consultationsToSync);
      
      if (synced.length > 0) {
        // Atualizar consultas com googleEventId no servidor PHP/MySQL
        // Isso seria feito no dataService
        setSyncStatus(`${synced.length} consulta(s) sincronizada(s) com sucesso!`);
        if (refresh) refresh();
      } else {
        setSyncStatus('Todas as consultas já estão sincronizadas');
      }
    } catch (error: any) {
      console.error('Erro ao sincronizar:', error);
      setSyncStatus(`Erro ao sincronizar: ${error.message}`);
    } finally {
      setIsSyncing(false);
      setTimeout(() => setSyncStatus(null), 5000);
    }
  };

  const handleAddToCalendar = async (e: React.MouseEvent, c: any) => {
    e.stopPropagation();
    
    if (isGoogleCalendarConnected && user?.uid) {
      // Usar API do Google Calendar se conectado
      try {
        const event = createConsultationEvent(
          c.pacienteNome,
          c.tipo,
          c.dataIso,
          c.notes
        );
        await createGoogleCalendarEvent(user.uid, event);
        alert('Consulta adicionada ao Google Calendar com sucesso!');
        if (refresh) refresh();
      } catch (error: any) {
        console.error('Erro ao adicionar ao Google Calendar:', error);
        // Fallback para método manual
        openGoogleCalendar(createConsultationEvent(c.pacienteNome, c.tipo, c.dataIso, c.notes));
      }
    } else {
      // Método manual (abre Google Calendar em nova aba)
      const event = createConsultationEvent(
        c.pacienteNome,
        c.tipo,
        c.dataIso,
        c.notes
      );
      openGoogleCalendar(event);
    }
  };

  return (
    <div className="ns-page">
      <div className="ns-page-header">
        <div>
          <h2>Agenda</h2>
          <p>Visualize e organize suas consultas {viewMode === 'week' ? 'da semana' : 'do mês'}.</p>
        </div>
        <div className="ns-page-header-actions">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => setViewMode('week')}
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                background: viewMode === 'week' ? 'var(--theme-primary)' : 'var(--page-surface)',
                color: viewMode === 'week' ? 'var(--page-surface)' : '#0f172a',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Semana
            </button>
            <button
              type="button"
              onClick={() => setViewMode('month')}
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                background: viewMode === 'month' ? 'var(--theme-primary)' : 'var(--page-surface)',
                color: viewMode === 'month' ? 'var(--page-surface)' : '#0f172a',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Mês
            </button>
          </div>
          {isGoogleCalendarConnected ? (
            <>
              <Button 
                variant="outline" 
                onClick={syncToGoogleCalendar}
                disabled={isSyncing}
                style={{ 
                  background: isSyncing ? '#cbd5e1' : undefined,
                  cursor: isSyncing ? 'not-allowed' : 'pointer'
                }}
              >
                {isSyncing ? '⏳ Sincronizando...' : '🔄 Sincronizar'}
              </Button>
              <Button variant="outline" onClick={disconnectGoogleCalendar}>
                🔌 Desconectar
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={connectGoogleCalendar}>
              🔗 Conectar Google Calendar
            </Button>
          )}
          <ConsultationNotificationSettings />
          <Button onClick={() => setModalOpen(true)}>Nova Consulta</Button>
        </div>
      </div>

      <ConsultationNotification consultas={consultas} />

      {syncStatus && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          background: syncStatus.includes('Erro') ? '#fee2e2' : '#d1fae5',
          color: syncStatus.includes('Erro') ? '#991b1b' : '#065f46',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: 500
        }}>
          {syncStatus}
        </div>
      )}

      {isGoogleCalendarConnected && (
        <Card
          title="✅ Google Calendar Conectado"
          subtitle="Suas consultas podem ser sincronizadas automaticamente"
        >
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--page-text-muted)' }}>
            Clique em "Sincronizar" para adicionar suas consultas ao Google Calendar.
            Novas consultas serão automaticamente adicionadas quando criadas.
          </p>
        </Card>
      )}

      <Card
        title="Consultas da semana"
        subtitle="Dados reais dos próximos dias"
      >
        <div className="ns-schedule-legend">
          <span className="ns-legend-item"><span className="ns-dot confirmed"></span> Confirmada</span>
          <span className="ns-legend-item"><span className="ns-dot pending"></span> Pendente</span>
          <span className="ns-legend-item"><span className="ns-dot" style={{ background: '#8b5cf6' }}></span> Primeira Consulta</span>
          <span className="ns-legend-item"><span className="ns-dot" style={{ background: 'var(--theme-primary)' }}></span> Retorno</span>
        </div>

        {loading ? (
          <p>Carregando agenda...</p>
        ) : viewMode === 'week' ? (
          <div className="ns-week-grid">
            {['Seg', 'Ter', 'Qua', 'Qui', 'Sex'].map((day) => (
              <div key={day} className="ns-week-column">
                <div className="ns-week-header">{day}</div>
                <div className="ns-week-body">
                  {grouped[day] && grouped[day].length > 0 ? (
                    grouped[day].map((c) => {

                      return (
                        <div
                          key={c.id}
                          className={`ns-week-slot ns-week-slot-${c.status === 'confirmada' ? 'confirmed' : 'pending'} ns-week-slot-${c.tipo}`}
                          style={{
                            borderLeftColor: c.tipo === 'primeira' ? '#8b5cf6' : '#0ea5e9',
                            background: c.tipo === 'primeira' 
                              ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(139, 92, 246, 0.04))'
                              : c.status === 'confirmada'
                              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.04))'
                              : 'linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(245, 158, 11, 0.04))'
                          }}
                        >
                          <div className="ns-week-slot-header">
                            <span className="ns-week-time">
                              {formatHora(c.dataIso)}
                            </span>
                            {c.status === 'aguardando' && (
                              <span className="ns-week-status-badge" title="Aguardando confirmação">⏳</span>
                            )}
                          </div>
                          <span className="ns-week-patient">
                            {c.pacienteNome}
                          </span>
                          <span className="ns-week-type">
                            {c.tipo === 'primeira' ? '1ª Consulta' : 'Retorno'}
                          </span>

                          <button
                            className="ns-week-calendar-btn"
                            onClick={(e) => handleAddToCalendar(e, c)}
                            title={isGoogleCalendarConnected ? "Adicionar ao Google Calendar (sincronizado)" : "Adicionar ao Google Calendar"}
                            aria-label="Adicionar ao Google Calendar"
                          >
                            📅
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="ns-week-slot ns-week-slot-empty">
                      <span className="ns-week-time">-</span>
                      <span className="ns-week-patient">
                        Livre
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
              <div key={day} style={{ 
                padding: '8px', 
                background: 'var(--page-surface-hover)', 
                borderRadius: '6px', 
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '12px',
                color: 'var(--page-text-muted)'
              }}>
                {day}
              </div>
            ))}
            {Array.from({ length: 35 }).map((_, index) => {
              const date = new Date(currentMonth);
              date.setDate(1);
              const firstDay = date.getDay();
              const dayNumber = index - firstDay + 1;
              const currentDate = new Date(date);
              currentDate.setDate(dayNumber);
              
              const dayConsultas = consultas.filter(c => {
                const consultaDate = new Date(c.dataIso);
                return consultaDate.toDateString() === currentDate.toDateString();
              });

              return (
                <div
                  key={index}
                  style={{
                    minHeight: '80px',
                    padding: '4px',
                    background: dayNumber > 0 && dayNumber <= new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
                      ? 'var(--page-surface)'
                      : 'var(--page-surface-hover)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    position: 'relative'
                  }}
                >
                  {dayNumber > 0 && dayNumber <= new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate() && (
                    <>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--page-text-muted)', marginBottom: '4px' }}>
                        {dayNumber}
                      </div>
                      {dayConsultas.map(c => (
                        <div
                          key={c.id}
                          style={{
                            fontSize: '10px',
                            padding: '2px 4px',
                            marginBottom: '2px',
                            background: c.tipo === 'primeira' ? '#8b5cf6' : '#0ea5e9',
                            color: 'white',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          title={`${c.pacienteNome} - ${formatHora(c.dataIso)}`}
                        >
                          {formatHora(c.dataIso)} {c.pacienteNome.substring(0, 10)}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <CreateConsultationModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

